import React, { useState } from 'react';
import { Grid, Container } from '@material-ui/core';
import {
  BoxV2 as Box,
  Table,
  FilterType,
  TableFilter,
  useTable,
  useFilters,
} from 'portal-commons';

import { CspListingRow } from '../components';
import { getCspListApi } from '../apiServices';
import '../assets/csp-listing-module.scss';
import { Loader, FilterToggleButton } from '../../../../shared_elements';
import { Csp } from '../types';
import { getCspNameAutocompleteOptions } from '@/apis/services';
import { useRcsVisibility } from '@/hooks/useRcsVisibility';

const CSP_ENTITY_TYPE = [
  { label: 'Publicly Traded Company', value: 'PUBLIC_PROFIT' },
  { label: 'Private Company', value: 'PRIVATE_PROFIT' },
  { label: 'Charity / Non-Profit Organization', value: 'NON_PROFIT' },
];

const FILTER_CONFIG = {
  companyName: {
    type: FilterType.Text,
    label: 'CSP Legal Name',
    placeholder: 'Enter CSP Legal Name',
    suggestions: [],
  },
  displayName: {
    type: FilterType.Text,
    label: 'CSP DBA Name',
    placeholder: 'Enter CSP DBA Name',
    suggestions: [],
  },
  entityType: {
    type: FilterType.Dropdown,
    options: CSP_ENTITY_TYPE.map((option) => ({
      label: option.value,
      value: option.value,
    })),
    label: 'Entity Type',
  },
};

const FILTER_LOADERS = {
  suggestion: {
    displayName: {
      minLength: 2,
      load: (value: string) =>
        getCspNameAutocompleteOptions('displayName', value),
    },
    companyName: {
      minLength: 2,
      load: (value: string) =>
        getCspNameAutocompleteOptions('companyName', value),
    },
  },
};

const headRows = [
  { id: 'companyName', label: 'CSP LEGAL NAME', sortable: true },
  { id: 'displayName', label: 'CSP DBA NAME', sortable: true },
  { id: 'country', label: 'COUNTRY OF REGISTRATION', sortable: true },
  { id: 'entityType', label: 'ENTITY TYPE', sortable: true },
  { id: 'activeCampaignCount', label: 'NO. OF CAMPAIGNS', sortable: false },
];

const CspListing = () => {
  const isRcsVisible = useRcsVisibility();

  const filter = useFilters({
    configs: FILTER_CONFIG,
    loaders: FILTER_LOADERS,
  });

  const { appliedFilters, additionalFilters, candidateFilters } = filter;
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  const table = useTable<Csp>(
    async (tableFilter) => {
      const channelIdValue = isRcsVisible ? null : '10DLC';
      const query = {
        ...tableFilter,
        ...additionalFilters,
        ...appliedFilters,
        channelId: channelIdValue,
      };
      const response = await getCspListApi(query);
      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        page: response?.page || 1,
        rowsPerPage: response?.recordsPerPage || 10,
      };
    },
    {
      deps: [appliedFilters, additionalFilters],
      defaultFilter: {
        sortField: 'displayName',
        ascendingOrder: false,
      },
    }
  );

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prev) => !prev);
  };

  return (
    <section className="csp-listing-section page-content">
      {table.pageLoading ? (
        <Loader />
      ) : (
        <Container
          maxWidth={false}
          className="csp-listing-container"
          data-testid="cspListing"
        >
          <Box>
            <div className="title-block">
              <h2>{!table.pageLoading ? `${table.total} CSPs` : 'CSPs'}</h2>
            </div>
          </Box>
          <Box sx={{ mt: 's', mb: 's' }}>
            <Box>
              <FilterToggleButton
                visible={isFilterVisible}
                onClick={toggleFilterVisibility}
              />
            </Box>
            {isFilterVisible && (
              <TableFilter
                portal="mno"
                configs={filter.configs}
                candidateValues={candidateFilters}
                appliedValues={appliedFilters}
                onCandidateValuesChange={filter.handleEdit}
                onAppliedValuesChange={filter.handleApply}
                className="filters"
                data-testid="csps"
              />
            )}
          </Box>
          <Grid container justifyContent="center" spacing={0}>
            <Table
              testId="cspTable"
              loading={table.loading}
              headRows={headRows}
              emptyState="no brands to view"
              tableData={table.tableData?.map((data) => (
                <CspListingRow csp={data} key={data.uid} />
              ))}
              handleChangePage={table.handleChangePage}
              createSortHandler={table.handleSorting}
              filter={table.filter}
              pagination={table.pagination}
            />
          </Grid>
        </Container>
      )}
    </section>
  );
};

export default CspListing;
